import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, ENVIRONMENT_INITIALIZER, Provider, importProvidersFrom, inject } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  Router,
  provideRouter,
  withComponentInputBinding,
  withHashLocation,
  withNavigationErrorHandler,
  withRouterConfig,
} from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { NavigationActionTiming, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { FormlyModule } from '@ngx-formly/core';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import {
  RktFormCheckboxGroupComponent,
  RktFormDatepickerComponent,
  RktFormFieldWrapperComponent,
  RktFormInputAddessComponent,
  RktFormInputCardComponent,
  RktFormInputComponent,
  RktFormInputPasswordComponent,
  RktFormInputPhoneComponent,
  RktFormInputSecureComponent,
  RktFormRadioGroupComponent,
  RktFormRepeatComponent,
  RktFormRepeatSectionComponent,
  RktFormSelectComponent,
  RktFormSelectCountryComponent,
  RktFormSelectGroupComponent,
  RktFormSelectMultiComponent,
  RktFormSelectStateComponent,
  RktFormTextareaComponent,
  RktFormTimepickerComponent,
  RktFormlyFieldGroupComponent,
  cardNumberValidator,
  countryCodeValidator,
  minLengthValidationMessage,
  phoneNumberValidator,
  requiredValidationMessage,
  stateCodeValidator,
} from '@rocketfinancialcorp/rocket-ui/form';
import {
  RktIconRegistryService,
  accounting,
  ach,
  aggregation,
  arrowDown,
  arrowLeft,
  arrowRight,
  arrowUp,
  attachment,
  bank,
  beneficiary,
  brainstorm,
  briefcase,
  bulb,
  businessman,
  calendar,
  campaign,
  card,
  cashInHand,
  chat,
  check,
  checkboxOff,
  checkboxOn,
  chevronDown,
  chevronLeft,
  chevronRight,
  chevronUp,
  close,
  company,
  copy,
  dashboard,
  depositAccounts,
  dispute,
  document,
  done,
  doubleArrowLeft,
  doubleArrowRight,
  download,
  driverLicense,
  edit,
  error,
  externalLink,
  eye,
  eyeSlash,
  fantasy,
  filter,
  flinksIcon,
  graphReport,
  identificationChecked,
  info,
  insight,
  link,
  list,
  loading,
  location,
  lock,
  mail,
  mailing,
  minusSign,
  moneyBag,
  moneyRequest,
  monitor,
  moveMoney,
  myAccount,
  newDocument,
  note,
  notification,
  passport,
  phone,
  plus,
  profile,
  report,
  request,
  resend,
  resume,
  reverse,
  sameDay,
  save,
  schedule,
  search,
  send,
  shipping,
  star,
  suspend,
  team,
  tiles,
  times,
  transfer,
  trash,
  upload,
  user,
  viewFile,
  wallClock,
} from '@rocketfinancialcorp/rocket-ui/icon';

import { AccessControlGuard, AuthGuard, CanDeactivateGuard, FeatureToggleGuard, NoAuthGuard } from '@shared/guards';
import { ErrorInterceptor, JwtInterceptor, LoaderInterceptor, WatcherInterceptor } from '@shared/interceptors';
import {
  APP_ENV_CONFIG,
  ChunkLoadRecoveryService,
  LoaderService,
  captureChunkLoadRecovery,
  getEnvironment,
  handleChunkNavigationError,
  sentryProviders,
} from '@shared/services';
import {
  AdaRegistrationDetailsEffects,
  AdaRegistrationEffects,
  AggregatedAccountBalanceEffects,
  AuditEffects,
  AuthEffects,
  BusinessAccountBeneficiaryEffects,
  CardAccountEffects,
  CounterpartyEffects,
  CustomerBeneficiaryEffects,
  DocumentEffects,
  ExceptionManagementEffects,
  FeatureManagementEffects,
  FinancialAccountsEffects,
  IndustryEffects,
  MessagesEffects,
  MocketBankAccountEffects,
  ModalsEffects,
  ROOT_REDUCERS,
  ReportEffects,
  TransactionFormEffects,
  TransactionsEffects,
  counterpartyFeature,
  ewaFeature,
  exceptionManagementFeature,
  featureManagementFeature,
  financialAccountFeature,
  fromAdaRegistration,
  fromAggregatedAccountBalance,
  fromAudit,
  fromAuth,
  fromBusinessAccount,
  fromCounterparty,
  fromCustomer,
  fromDocument,
  fromFinancialAccount,
  fromIndustries,
  fromMocketBank,
  fromReport,
  linxFeature,
  metaReducers,
  transactionBatchFeature,
  transactionFeature,
  transactionFormFeature,
} from '@shared/store';
import { TransactionSolutionEffects } from '@shared/store/transaction-store/transaction-solution.effects';
import { transactionSolution } from '@shared/store/transaction-store/transaction-solution.reducer';
import { SetPageTitle, getAppBaseHref } from '@shared/utils';

import { APP_ROUTES } from './portal-client-app.routes';

interface RktIcon<T> {
  name: T;
  data: string;
}

const clock: RktIcon<'clock'> = {
  name: 'clock',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 21 21"><path fill="currentColor" d="M10.5 3C6.35775 3 3 6.35775 3 10.5c0 4.1423 3.35775 7.5 7.5 7.5 4.1423 0 7.5-3.3577 7.5-7.5C18 6.35775 14.6423 3 10.5 3Zm2.4698 11.0303L9.75 10.8105V6h1.5v4.1895l2.7803 2.7803-1.0605 1.0605Z"/></svg>`,
};

const exceptionsList: RktIcon<'exceptions-list'> = {
  name: 'exceptions-list',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="m5.586 3-2 2H2v2h2.414L7 4.414 5.586 3ZM9 5v2h13V5H9ZM5.586 9l-2 2H2v2h2.414L7 10.414 5.586 9ZM9 11v2h13v-2H9Zm-5 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm5 .5v2h13v-2H9Z"/></svg>`,
};

const attachmentImg: RktIcon<'attachment-img'> = {
  name: 'attachment-img',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="currentColor" d="M16.667 3.333H3.334c-.921 0-1.667.746-1.667 1.667v10c0 .92.746 1.666 1.667 1.666h13.333c.92 0 1.667-.745 1.667-1.666V5c0-.921-.746-1.667-1.667-1.667ZM8.334 6.666a.834.834 0 1 1 0 1.668.834.834 0 0 1 0-1.668Zm6.666 7.5H5.02a.416.416 0 0 1-.329-.672l2.075-2.668a.416.416 0 0 1 .65-.01l1.752 2.109 2.582-3.327a.416.416 0 0 1 .662.006l2.923 3.896a.417.417 0 0 1-.334.666Z"/></svg>`,
};

const attachmentPdf: RktIcon<'attachment-pdf'> = {
  name: 'attachment-pdf',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="currentColor" d="M5.556 1c-.973 0-1.778.815-1.778 1.8v4.5C2.796 7.3 2 8.105 2 9.1v4.5c0 .995.796 1.8 1.778 1.8v1.8c0 .985.805 1.8 1.778 1.8h10.666c.973 0 1.778-.815 1.778-1.8V5.873a.906.906 0 0 0-.26-.637l-3.924-3.972A.884.884 0 0 0 13.187 1H5.556Zm0 1.8h7.11v2.7c0 .497.399.9.89.9h2.666v.9H5.556V2.8ZM3.778 9.1H5.11c.736 0 1.333.605 1.333 1.35 0 .745-.597 1.35-1.333 1.35h-.444v1.8h-.89V9.1Zm8 0h2.666v.9h-1.777v.9h1.421v.9h-1.421v1.8h-.89V9.1Zm-4.445.021h1.448c1.406 0 2.108.856 2.108 1.895v.694c0 1.052-.703 1.89-2.12 1.89H7.333V9.121ZM4.667 10v.9h.444a.447.447 0 0 0 .445-.45c0-.248-.2-.45-.445-.45h-.444Zm3.555.021V12.7h.547c.562 0 1.231-.172 1.231-.99v-.694c0-.823-.662-.995-1.219-.995h-.559ZM5.556 15.4h10.666v1.8H5.556v-1.8Z"/></svg>`,
};

const bankLink: RktIcon<'bank-link'> = {
  name: 'bank-link',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 23"><path fill="currentColor" d="M17.6367 14.3789C17.8361 14.3868 18.0356 14.4206 18.2275 14.4814L16.8906 15.8135L17.1797 16.9639L18.334 17.251L19.6699 15.9189C19.9144 16.6837 19.7341 17.5542 19.125 18.1611C18.535 18.7493 17.8248 18.8021 17.1572 18.5098L12.9688 22.6836C12.5461 23.1048 11.8611 23.1048 11.4385 22.6836C11.016 22.2624 11.0159 21.5803 11.4385 21.1592L15.6279 16.9854C15.3345 16.3202 15.3869 15.6133 15.9766 15.0254C16.4333 14.5705 17.0387 14.3555 17.6367 14.3789ZM18.8184 21.3662C19.1934 21.74 19.1934 22.346 18.8184 22.7197C18.4437 23.0934 17.835 23.0935 17.46 22.7197L15.7227 20.9873L17.0801 19.6348L18.8184 21.3662ZM12.2031 21.5625C12.0043 21.5628 11.8428 21.7237 11.8428 21.9219C11.8429 22.12 12.0043 22.281 12.2031 22.2812C12.4021 22.2812 12.5643 22.1201 12.5645 21.9219C12.5645 21.7235 12.4022 21.5625 12.2031 21.5625ZM9.60059 18.2002V19.7998H0.800781C0.588608 19.7998 0.384404 19.7155 0.234375 19.5654C0.0845198 19.4154 6.20469e-06 19.212 0 19C0 18.788 0.084529 18.5846 0.234375 18.4346C0.384404 18.2845 0.588608 18.2002 0.800781 18.2002H9.60059ZM12.5645 15.0938L12.9248 16.1719L14.1875 17.4297L13.4658 18.1484L12.2031 16.8906L11.1221 16.5312L10.4004 15.0938L11.1221 14.375L12.5645 15.0938ZM10.4004 0.599609C10.5323 0.599709 10.6621 0.632886 10.7783 0.695312L19.5068 5.46094C19.6529 5.52151 19.7774 5.62437 19.8652 5.75586C19.9531 5.8874 20 6.04201 20 6.2002C19.9999 6.41229 19.9156 6.61565 19.7656 6.76562C19.6156 6.91553 19.4123 7 19.2002 7H17.6006V13.4004H16V7H14.4004V13.4004H12.8008V7H11.2002V13.4004H9.60059V7H8V15H9.60059V16.5996H7.32715C7.24087 16.6139 7.15269 16.6138 7.06641 16.5996H4.12695C4.04061 16.6139 3.95255 16.6139 3.86621 16.5996H2.40039C2.18822 16.5996 1.98499 16.5153 1.83496 16.3652C1.68498 16.2152 1.60059 16.0119 1.60059 15.7998C1.60063 15.5877 1.68497 15.3844 1.83496 15.2344C1.98498 15.0844 2.18826 15 2.40039 15H3.2002V7H1.60059C1.38852 7 1.18516 6.91552 1.03516 6.76562C0.885179 6.61565 0.800839 6.41229 0.800781 6.2002C0.800781 6.042 0.847672 5.8874 0.935547 5.75586C1.0234 5.62436 1.14786 5.52151 1.29395 5.46094L10.0176 0.698242C10.1349 0.633959 10.2666 0.599886 10.4004 0.599609ZM4.80078 7V15H6.40039V7H4.80078ZM10.4004 3C9.95885 3 9.60068 3.35828 9.60059 3.7998C9.60059 4.2414 9.95879 4.59961 10.4004 4.59961C10.842 4.5996 11.2002 4.2414 11.2002 3.7998C11.2001 3.35829 10.8419 3.00001 10.4004 3Z"/></svg>`,
};

const developerTool: RktIcon<'developer-tool'> = {
  name: 'developer-tool',
  data: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 22 20"><path fill="currentColor" d="M2.2001 0.400024L0.600098 2.00002L2.2001 5.20002L4.6001 6.00002L7.4001 8.80002L9.0001 7.20002L6.2001 4.40002L5.4001 2.00002L2.2001 0.400024ZM16.6532 0.407837C15.3264 0.355374 13.9838 0.834099 12.9704 1.8469C11.6616 3.15575 11.5444 4.72996 12.1954 6.21096L2.90322 15.5031C1.96562 16.4408 1.96562 17.9593 2.90322 18.8969C3.84082 19.8345 5.35937 19.8345 6.29697 18.8969L15.5892 9.60471C17.0701 10.2555 18.6445 10.1374 19.9532 8.82815C21.3044 7.47695 21.705 5.53992 21.1626 3.83752L18.1985 6.80315L15.6376 6.16252L14.997 3.60159L17.9626 0.637524C17.5368 0.502124 17.0955 0.425324 16.6532 0.407837ZM15.4173 12.1094L12.4063 15.1203L16.261 18.9766C17.093 19.8086 18.4423 19.8086 19.2735 18.9766C20.1055 18.1446 20.1055 16.7961 19.2735 15.9641L15.4173 12.1094ZM4.6001 16.4C5.0417 16.4 5.4001 16.7584 5.4001 17.2C5.4001 17.6416 5.0417 18 4.6001 18C4.1585 18 3.8001 17.6416 3.8001 17.2C3.8001 16.7584 4.1585 16.4 4.6001 16.4Z"/></svg>`,
};

const mocketbnk: RktIcon<'mocketbnk'> = {
  name: 'mocketbnk',
  data: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M3.19948 3.2002C2.31548 3.2002 1.59948 3.9162 1.59948 4.8002V8.8002V9.6002V17.7377L3.19948 16.1377V9.6002H20.7995V19.2002H6.26198L4.66198 20.8002H22.3995V9.6002V8.8002V4.8002C22.3995 3.9162 21.6835 3.2002 20.7995 3.2002H3.19948ZM3.99948 5.6002C4.44108 5.6002 4.79948 5.9586 4.79948 6.4002C4.79948 6.8418 4.44108 7.2002 3.99948 7.2002C3.55788 7.2002 3.19948 6.8418 3.19948 6.4002C3.19948 5.9586 3.55788 5.6002 3.99948 5.6002ZM7.19948 5.6002C7.64108 5.6002 7.99948 5.9586 7.99948 6.4002C7.99948 6.8418 7.64108 7.2002 7.19948 7.2002C6.75788 7.2002 6.39948 6.8418 6.39948 6.4002C6.39948 5.9586 6.75788 5.6002 7.19948 5.6002ZM9.59948 5.6002H20.7995V7.2002H9.59948V5.6002ZM17.5995 11.2002C17.3809 11.2004 17.1647 11.2454 16.9642 11.3323C16.7637 11.4193 16.5832 11.5464 16.4337 11.7059C16.2842 11.8653 16.169 12.0537 16.0951 12.2593C16.0213 12.465 15.9903 12.6837 16.0042 12.9018L13.5073 14.5658C13.2872 14.457 13.045 14.4004 12.7995 14.4002C12.409 14.4008 12.0322 14.5442 11.7401 14.8033L9.56511 14.0783C9.49104 13.7177 9.29486 13.3936 9.00965 13.1608C8.72444 12.9279 8.36766 12.8006 7.99948 12.8002C7.75438 12.8005 7.51264 12.8572 7.2929 12.9657C7.07316 13.0743 6.8813 13.2319 6.73213 13.4264C6.58295 13.6208 6.48045 13.847 6.43253 14.0874C6.3846 14.3277 6.39254 14.5759 6.45573 14.8127L1.83386 19.4346C1.75708 19.5083 1.69578 19.5966 1.65355 19.6943C1.61132 19.792 1.58901 19.8971 1.58793 20.0036C1.58685 20.11 1.60701 20.2156 1.64725 20.3141C1.68748 20.4127 1.74697 20.5022 1.82223 20.5774C1.89749 20.6527 1.98702 20.7122 2.08556 20.7524C2.1841 20.7927 2.28968 20.8128 2.39611 20.8117C2.50254 20.8107 2.60769 20.7884 2.70539 20.7461C2.8031 20.7039 2.89139 20.6426 2.96511 20.5658L7.58698 15.9439C7.72145 15.9806 7.86012 15.9995 7.99948 16.0002C8.38997 15.9996 8.76675 15.8562 9.05886 15.5971L11.2339 16.3221C11.3079 16.6827 11.5041 17.0068 11.7893 17.2396C12.0745 17.4725 12.4313 17.5998 12.7995 17.6002C13.018 17.6 13.2342 17.555 13.4347 17.4681C13.6352 17.3811 13.8158 17.254 13.9652 17.0945C14.1147 16.9351 14.23 16.7467 14.3038 16.5411C14.3777 16.3354 14.4087 16.1167 14.3948 15.8986L16.8917 14.2346C17.1118 14.3434 17.354 14.4 17.5995 14.4002C18.0238 14.4002 18.4308 14.2316 18.7309 13.9316C19.0309 13.6315 19.1995 13.2245 19.1995 12.8002C19.1995 12.3758 19.0309 11.9689 18.7309 11.6688C18.4308 11.3688 18.0238 11.2002 17.5995 11.2002Z" /></svg>',
};

const refresh: RktIcon<'refresh'> = {
  name: 'refresh',
  data: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="M12 4c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8h-1.6c0 3.529-2.871 6.4-6.4 6.4A6.407 6.407 0 0 1 5.6 12c0-3.529 2.871-6.4 6.4-6.4 1.765 0 3.362.72 4.52 1.88L14.4 9.6H20V4l-2.352 2.352A7.955 7.955 0 0 0 12 4Z"/></svg>',
};

const verified: RktIcon<'verified'> = {
  name: 'verified',
  data: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><path fill="currentColor" d="m20.305 12 .56-1.728a2.77 2.77 0 0 0-1.374-3.318l-1.617-.827-.827-1.617a2.768 2.768 0 0 0-3.318-1.374L12 3.695l-1.728-.56a2.77 2.77 0 0 0-3.318 1.374l-.827 1.618-1.618.827a2.77 2.77 0 0 0-1.374 3.318L3.695 12l-.56 1.728a2.77 2.77 0 0 0 1.374 3.318l1.618.827.826 1.617a2.768 2.768 0 0 0 3.318 1.374L12 20.305l1.728.56a2.77 2.77 0 0 0 3.318-1.374l.827-1.617 1.617-.827a2.768 2.768 0 0 0 1.374-3.318L20.305 12ZM11.1 15.973l-3.336-3.337 1.272-1.272 2.064 2.063 4.764-4.763 1.272 1.272-6.036 6.037Z"/></svg>',
};

export const iconsProvider: () => Provider = () => {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useValue: () =>
      inject(RktIconRegistryService).registerIcons([
        info,
        edit,
        arrowLeft,
        filter,
        trash,
        suspend,
        resume,
        bank,
        card,
        plus,
        check,
        times,
        chevronUp,
        chevronDown,
        dashboard,
        moveMoney,
        myAccount,
        team,
        campaign,
        location,
        mail,
        mailing,
        phone,
        profile,
        save,
        moneyBag,
        request,
        resend,
        calendar,
        send,
        moneyRequest,
        ach,
        transfer,
        note,
        close,
        reverse,
        monitor,
        document,
        sameDay,
        user,
        arrowRight,
        lock,
        upload,
        identificationChecked,
        passport,
        driverLicense,
        company,
        accounting,
        businessman,
        done,
        depositAccounts,
        dispute,
        briefcase,
        beneficiary,
        insight,
        cashInHand,
        shipping,
        star,
        minusSign,
        error,
        notification,
        download,
        link,
        copy,
        externalLink,
        bulb,
        attachment,
        eye,
        eyeSlash,
        tiles,
        list,
        brainstorm,
        fantasy,
        graphReport,
        chat,
        clock,
        schedule,
        newDocument,
        report,
        wallClock,
        viewFile,
        exceptionsList,
        attachmentImg,
        attachmentPdf,
        refresh,
        verified,
        star,
        chevronLeft,
        chevronRight,
        doubleArrowLeft,
        doubleArrowRight,
        checkboxOff,
        checkboxOn,
        arrowDown,
        arrowUp,
        search,
        flinksIcon,
        loading,
        aggregation,
        bankLink,
        developerTool,
        mocketbnk,
        schedule,
      ]),
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideEnvironmentNgxMask(),
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withInterceptors([JwtInterceptor])),
    importProvidersFrom(
      BrowserModule,
      ToastrModule.forRoot({
        preventDuplicates: true,
      }),
      FormlyModule.forRoot({
        types: [
          { name: 'formly-group', component: RktFormlyFieldGroupComponent },
          { name: 'input', component: RktFormInputComponent, wrappers: ['field-wrapper'] },
          { name: 'input-phone', component: RktFormInputPhoneComponent, wrappers: ['field-wrapper'] },
          { name: 'input-address', component: RktFormInputAddessComponent, wrappers: ['field-wrapper'] },
          { name: 'input-password', component: RktFormInputPasswordComponent, wrappers: ['field-wrapper'] },
          { name: 'input-card', component: RktFormInputCardComponent, wrappers: ['field-wrapper'] },
          { name: 'input-secure', component: RktFormInputSecureComponent, wrappers: ['field-wrapper'] },
          { name: 'number', extends: 'input', defaultOptions: { props: { type: 'number' } } },
          { name: 'select', component: RktFormSelectComponent, wrappers: ['field-wrapper'] },
          { name: 'select-country', component: RktFormSelectCountryComponent, wrappers: ['field-wrapper'] },
          { name: 'select-state', component: RktFormSelectStateComponent, wrappers: ['field-wrapper'] },
          { name: 'select-multi', component: RktFormSelectMultiComponent, wrappers: ['field-wrapper'] },
          { name: 'textarea', component: RktFormTextareaComponent, wrappers: ['field-wrapper'] },
          { name: 'radio-group', component: RktFormRadioGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'checkbox-group', component: RktFormCheckboxGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'select-group', component: RktFormSelectGroupComponent, wrappers: ['field-wrapper'] },
          { name: 'repeat', component: RktFormRepeatComponent, wrappers: ['field-wrapper'] },
          { name: 'repeat-section', component: RktFormRepeatSectionComponent, wrappers: ['field-wrapper'] },
          { name: 'datepicker', component: RktFormDatepickerComponent, wrappers: ['field-wrapper'] },
          { name: 'timepicker', component: RktFormTimepickerComponent, wrappers: ['field-wrapper'] },
        ],
        wrappers: [{ name: 'field-wrapper', component: RktFormFieldWrapperComponent }],
        validationMessages: [
          { name: 'required', message: requiredValidationMessage },
          { name: 'minLength', message: minLengthValidationMessage },
        ],
        validators: [
          { name: 'isValidPhoneNumber', validation: phoneNumberValidator },
          { name: 'isValidCountryCode', validation: countryCodeValidator },
          { name: 'isValidStateCode', validation: stateCodeValidator },
          { name: 'isValidCardNumber', validation: cardNumberValidator },
        ],
      }),
      StoreModule.forRoot(ROOT_REDUCERS, {
        metaReducers,
        runtimeChecks: {
          strictStateSerializability: true,
          strictActionSerializability: true,
          strictActionWithinNgZone: true,
          strictActionTypeUniqueness: true,
        },
      }),
      EffectsModule.forRoot([MessagesEffects, ModalsEffects]),
      StoreModule.forFeature(fromCounterparty.counterpartyFeatureKey, fromCounterparty.reducers),
      StoreModule.forFeature(fromDocument.documentFeatureKey, fromDocument.reducers),
      StoreModule.forFeature(fromCustomer.customerFeatureKey, fromCustomer.reducers),
      StoreModule.forFeature(fromAggregatedAccountBalance.aggregatedAccountBalanceFeatureKey, fromAggregatedAccountBalance.reducers),
      StoreModule.forFeature(fromBusinessAccount.businessAccountFeatureKey, fromBusinessAccount.reducers),
      StoreModule.forFeature(fromFinancialAccount.financialAccountFeatureKey, fromFinancialAccount.reducers),
      StoreModule.forFeature(fromMocketBank.mocketBankAccountFeatureKey, fromMocketBank.reducers),
      StoreModule.forFeature(fromAuth.authFeatureKey, fromAuth.reducers),
      StoreModule.forFeature(fromReport.reportFeatureKey, fromReport.reducers),
      StoreModule.forFeature(transactionFeature),
      StoreModule.forFeature(transactionFormFeature),
      StoreModule.forFeature(featureManagementFeature),
      StoreModule.forFeature(fromAudit.auditFeatureKey, fromAudit.reducers),
      StoreModule.forFeature(fromIndustries.industryFeatureKey, fromIndustries.reducers),
      StoreModule.forFeature(fromAdaRegistration.adaRegistrationFeatureKey, fromAdaRegistration.reducers),
      StoreModule.forFeature(exceptionManagementFeature),
      StoreModule.forFeature(counterpartyFeature),
      StoreModule.forFeature(transactionBatchFeature),
      StoreModule.forFeature(ewaFeature),
      StoreModule.forFeature(linxFeature),
      StoreModule.forFeature(financialAccountFeature),
      StoreModule.forFeature(transactionSolution),
      EffectsModule.forFeature([
        AuthEffects,
        CounterpartyEffects,
        DocumentEffects,
        CustomerBeneficiaryEffects,
        BusinessAccountBeneficiaryEffects,
        CardAccountEffects,
        TransactionsEffects,
        FeatureManagementEffects,
        TransactionFormEffects,
        FinancialAccountsEffects,
        ReportEffects,
        AggregatedAccountBalanceEffects,
        AuditEffects,
        IndustryEffects,
        AdaRegistrationEffects,
        AdaRegistrationDetailsEffects,
        ExceptionManagementEffects,
        TransactionSolutionEffects,
        MocketBankAccountEffects,
      ]),
      StoreRouterConnectingModule.forRoot({ navigationActionTiming: NavigationActionTiming.PostActivation }),
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: getEnvironment().production }),
    ),
    LoaderService,
    AuthGuard,
    NoAuthGuard,
    FeatureToggleGuard,
    CanDeactivateGuard,
    AccessControlGuard,
    Title,
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: APP_INITIALIZER, useFactory: SetPageTitle, deps: [Router, Title], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: WatcherInterceptor, multi: true },
    { provide: APP_BASE_HREF, useFactory: getAppBaseHref },
    { provide: APP_ENV_CONFIG, useFactory: getEnvironment },
    ...sentryProviders(),
    iconsProvider(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimations(),
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withHashLocation(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      // KOR-15102: a lazy route whose chunk was removed by a deploy fails here. This is the only
      // surface that can retry the import in place, by re-running the navigation. The logic lives
      // in handleChunkNavigationError so it is unit-testable; this provider is never instantiated
      // in a spec.
      withNavigationErrorHandler((event) => {
        const stage = handleChunkNavigationError(event, inject(ChunkLoadRecoveryService));

        if (stage) {
          captureChunkLoadRecovery(stage, event.url);
        }
      }),
    ),
  ],
};
